package com.schoolconnect.app.feature.classconversations

import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.datasource.network.request.ClassConversationCreateRequest
import com.schoolconnect.app.repository.classconversations.ClassConversationsRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.runtime.AppErrorCenter
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.LocalDate
import kotlin.time.Clock

class ClassConversationsViewModel(
    private val studentId: String? = null,
    private val schoolId: String = UserSessionStore.getHomeRoleSchoolId()
) : AutoCloseable {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var listJob: Job? = null
    private var detailJob: Job? = null
    private var mutationJob: Job? = null
    private val _uiState = MutableStateFlow(ClassConversationsUiState())
    val uiState: StateFlow<ClassConversationsUiState> = _uiState.asStateFlow()

    init {
        loadPage()
        loadClassSections()
    }

    fun onEvent(event: ClassConversationsUiEvent) {
        when (event) {
            ClassConversationsUiEvent.Refresh -> loadPage()
            is ClassConversationsUiEvent.QueryChanged -> {
                _uiState.update { it.copy(query = event.value, pageIndex = 0) }
                loadPage(debounce = true)
            }
            ClassConversationsUiEvent.FiltersReset -> {
                _uiState.update { it.copy(query = "", classSectionId = "", period = CONVERSATION_PERIOD_ALL, periodFrom = "", periodTo = "", pageIndex = 0) }
                loadPage()
            }
            is ClassConversationsUiEvent.ClassSectionChanged -> {
                _uiState.update { it.copy(classSectionId = event.classSectionId.trim(), pageIndex = 0) }
                loadPage()
            }
            is ClassConversationsUiEvent.PeriodChanged -> {
                _uiState.update { it.copy(period = event.value, periodFrom = if (event.value == "Custom Range") it.periodFrom else "", periodTo = if (event.value == "Custom Range") it.periodTo else "", pageIndex = 0) }
                loadPage()
            }
            is ClassConversationsUiEvent.CustomPeriodChanged -> {
                _uiState.update { it.copy(period = "Custom Range", periodFrom = event.from, periodTo = event.to, pageIndex = 0) }
                loadPage()
            }
            is ClassConversationsUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                loadPage()
            }
            is ClassConversationsUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(1, 100), pageIndex = 0) }
                loadPage()
            }
            is ClassConversationsUiEvent.SortChanged -> {
                _uiState.update { current ->
                    current.copy(
                        sortField = event.field,
                        sortAscending = if (current.sortField == event.field) !current.sortAscending else true,
                        pageIndex = 0
                    )
                }
                loadPage()
            }
            is ClassConversationsUiEvent.ViewRequested -> loadDetail(event.conversationId)
            ClassConversationsUiEvent.ViewDismissed -> _uiState.update { it.copy(selected = null, replies = emptyList(), detailErrorMessage = "") }
            is ClassConversationsUiEvent.ReplyRequested -> createReply(event.conversationId, event.replyText, event.attachmentUrl, event.attachmentFilename)
            is ClassConversationsUiEvent.CreateRequested -> createConversation(event.title, event.description, event.attachmentUrl, event.attachmentFilename, event.attachmentSizeBytes)
            is ClassConversationsUiEvent.UpdateRequested -> updateConversation(event.conversationId, event.title, event.description, event.attachmentUrl, event.attachmentFilename, event.attachmentSizeBytes, event.attachmentChanged)
            is ClassConversationsUiEvent.ModerateRequested -> moderate(event.conversationId, event.action, event.reason)
            is ClassConversationsUiEvent.DeleteRequested -> delete(event.conversationId, event.reason)
            is ClassConversationsUiEvent.DeleteReplyRequested -> deleteReply(event.conversationId, event.replyId, event.reason)
        }
    }

    private fun loadPage(debounce: Boolean = false) {
        listJob?.cancel()
        listJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            val periodRange = conversationPeriodRange(state.period, state.periodFrom, state.periodTo)
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            ClassConversationsRepository.loadPage(state.query, state.pageIndex, state.pageSize, studentId, state.classSectionId, periodRange.first, periodRange.second, state.sortField.toApiSortField(), if (state.sortAscending) "asc" else "desc", scope).fold(
                onSuccess = { result ->
                    val maxPage = ((result.totalCount - 1).coerceAtLeast(0) / state.pageSize)
                    if (state.pageIndex > maxPage) {
                        _uiState.update { it.copy(rows = emptyList(), totalCount = result.totalCount, pageIndex = maxPage) }
                        loadPage()
                    } else {
                        _uiState.update { it.copy(rows = result.items, totalCount = result.totalCount, pageIndex = state.pageIndex, isLoading = false) }
                    }
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isLoading = false, errorMessage = "") }
                    AppErrorCenter.emit(throwable, source = "ClassConversationsViewModel", fallbackMessage = "Unable to load conversations.")
                }
            )
        }
    }

    private fun loadClassSections() {
        if (schoolId.isBlank() || !studentId.isNullOrBlank()) return
        scope.launch {
            _uiState.update { it.copy(isClassSectionLoading = true, filterLookupError = "") }
            ClassSectionRepository.loadSchoolClassSectionLookup(schoolId).fold(
                onSuccess = { rows ->
                    val options = rows.mapNotNull { row ->
                        val id = row.id?.trim().orEmpty()
                        val label = listOf(row.className?.trim(), row.sectionName?.trim()).filterNot { it.isNullOrBlank() }.joinToString("-")
                        if (id.isBlank() || label.isBlank()) null else ConversationClassSectionOption(id, label)
                    }.distinctBy { it.id }
                        .sortedWith(compareBySmartText<ConversationClassSectionOption> { it.label })
                    _uiState.update { it.copy(classSectionOptions = options, isClassSectionLoading = false) }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isClassSectionLoading = false, filterLookupError = error.message.orEmpty().ifBlank { "Unable to load class filters." }) }
                }
            )
        }
    }

    private fun loadDetail(conversationId: String, preserveSelection: Boolean = false, finishMutationWhenLoaded: Boolean = false) {
        detailJob?.cancel()
        detailJob = scope.launch {
            _uiState.update {
                it.copy(
                    selected = if (preserveSelection) it.selected else null,
                    replies = if (preserveSelection) it.replies else emptyList(),
                    isDetailLoading = true,
                    detailErrorMessage = ""
                )
            }
            val detail = ClassConversationsRepository.loadDetail(conversationId)
            val replies = ClassConversationsRepository.loadReplies(conversationId)
            if (detail.isSuccess) {
                _uiState.update {
                    it.copy(
                        selected = detail.getOrThrow(),
                        replies = replies.getOrElse { emptyList() },
                        isDetailLoading = false,
                        isMutating = if (finishMutationWhenLoaded) false else it.isMutating,
                        replyResetGeneration = if (finishMutationWhenLoaded) it.replyResetGeneration + 1 else it.replyResetGeneration,
                        detailErrorMessage = replies.exceptionOrNull()?.message.orEmpty()
                    )
                }
            } else {
                _uiState.update {
                    it.copy(
                        isDetailLoading = false,
                        isMutating = if (finishMutationWhenLoaded) false else it.isMutating,
                        detailErrorMessage = detail.exceptionOrNull()?.message ?: "Unable to load conversation."
                    )
                }
            }
        }
    }

    private fun createReply(conversationId: String, replyText: String, attachmentUrl: String?, attachmentFilename: String?) {
        if (replyText.trim().isBlank()) return
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, mutationErrorMessage = "") }
            ClassConversationsRepository.createReply(conversationId, replyText, attachmentUrl, attachmentFilename).fold(
                onSuccess = {
                    loadDetail(conversationId, preserveSelection = true, finishMutationWhenLoaded = true)
                    loadPage()
                },
                onFailure = { throwable -> _uiState.update { it.copy(isMutating = false, mutationErrorMessage = throwable.message ?: "Unable to post reply.") } }
            )
        }
    }

    private fun createConversation(title: String, description: String, attachmentUrl: String?, attachmentFilename: String?, attachmentSizeBytes: Int?) {
        if (studentId.isNullOrBlank() || title.trim().isBlank() || description.trim().isBlank()) return
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, mutationErrorMessage = "") }
            ClassConversationsRepository.create(ClassConversationCreateRequest(title.trim(), description.trim(), studentId = studentId, attachmentUrl = attachmentUrl, attachmentFilename = attachmentFilename, attachmentSizeBytes = attachmentSizeBytes)).fold(
                onSuccess = { _uiState.update { it.copy(isMutating = false) }; loadPage() },
                onFailure = { throwable -> _uiState.update { it.copy(isMutating = false, mutationErrorMessage = throwable.message ?: "Unable to post conversation.") } }
            )
        }
    }

    private fun updateConversation(conversationId: String, title: String, description: String, attachmentUrl: String?, attachmentFilename: String?, attachmentSizeBytes: Int?, attachmentChanged: Boolean) {
        if (conversationId.trim().isBlank() || title.trim().isBlank() || description.trim().isBlank()) return
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, mutationErrorMessage = "") }
            ClassConversationsRepository.update(conversationId, title, description, attachmentUrl, attachmentFilename, attachmentSizeBytes, attachmentChanged).fold(
                onSuccess = { _uiState.update { it.copy(isMutating = false) }; loadPage() },
                onFailure = { throwable -> _uiState.update { it.copy(isMutating = false, mutationErrorMessage = throwable.message ?: "Unable to update conversation.") } }
            )
        }
    }

    private fun moderate(conversationId: String, action: String, reason: String?) {
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, mutationErrorMessage = "") }
            ClassConversationsRepository.moderate(conversationId, action, reason).fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            isMutating = false,
                            selected = null,
                            replies = emptyList(),
                            isDetailLoading = false,
                            detailErrorMessage = ""
                        )
                    }
                    loadPage()
                },
                onFailure = { throwable -> _uiState.update { it.copy(isMutating = false, mutationErrorMessage = throwable.message ?: "Unable to update conversation.") } }
            )
        }
    }

    private fun delete(conversationId: String, reason: String?) {
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, mutationErrorMessage = "") }
            ClassConversationsRepository.delete(conversationId, reason).fold(
                onSuccess = { _uiState.update { it.copy(isMutating = false, selected = null) }; loadPage() },
                onFailure = { throwable -> _uiState.update { it.copy(isMutating = false, mutationErrorMessage = throwable.message ?: "Unable to delete conversation.") } }
            )
        }
    }

    private fun deleteReply(conversationId: String, replyId: String, reason: String?) {
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, mutationErrorMessage = "") }
            ClassConversationsRepository.deleteReply(conversationId, replyId, reason).fold(
                onSuccess = {
                    loadDetail(conversationId, preserveSelection = true, finishMutationWhenLoaded = true)
                    loadPage()
                },
                onFailure = { throwable -> _uiState.update { it.copy(isMutating = false, mutationErrorMessage = throwable.message ?: "Unable to delete reply.") } }
            )
        }
    }

    override fun close() {
        scLog("[DATA_RETRIEVE_SC] ClassConversationsViewModel clear")
        listJob?.cancel(); detailJob?.cancel(); mutationJob?.cancel(); scope.cancel()
    }
}

private fun conversationPeriodRange(period: String, customFrom: String, customTo: String): Pair<String?, String?> {
    if (period == "Custom Range") {
        val from = appDateInputToApi(customFrom)?.let { "${it}T00:00:00" }
        val to = appDateInputToApi(customTo)?.let { "${it}T23:59:59.999999" }
        return from to to
    }
    val today = Clock.System.now().toLocalDateTime(TimeZone.UTC).date
    return when (period) {
        CONVERSATION_PERIOD_TODAY -> dayStart(today) to dayEnd(today)
        CONVERSATION_PERIOD_LAST_7_DAYS -> dayStart(today.minus(6, DateTimeUnit.DAY)) to dayEnd(today)
        CONVERSATION_PERIOD_THIS_MONTH -> dayStart(LocalDate(today.year, today.month, 1)) to dayEnd(today)
        else -> null to null
    }
}

private fun dayStart(date: LocalDate): String = "${date}T00:00:00"
private fun dayEnd(date: LocalDate): String = "${date}T23:59:59.999999"
